import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { fetchMapViewPreference, saveMapViewPreference } from '../api/mapViewPreferenceApi';
import { isGameMap3dAvailable } from '../game-map/featureFlag';
import { runtime } from '../utils/runtime';
import {
  MAP_VIEW_3D,
  MAP_VIEW_CLASSIC,
  isMapViewPreference,
  mapViewStorageKey,
  normalizeMapViewPreference,
  readMapViewQaOverride,
  resolveInitialMapView,
  urlWithoutMapViewOverride,
} from './mapViewState.mjs';

const MapViewContext = createContext(null);

function readLocalPreference(storageKey) {
  try {
    return window.localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

function writeLocalPreference(storageKey, preference) {
  try {
    window.localStorage.setItem(storageKey, preference);
  } catch {}
}

export function MapViewProvider({ children }) {
  const available = isGameMap3dAvailable();
  const storageKey = mapViewStorageKey(runtime.user_id);
  const queryPreference = readMapViewQaOverride(window.location.search);
  const [view, setViewState] = useState(() => resolveInitialMapView({
    available,
    queryPreference,
    serverPreference: runtime.mapViewPreference,
    localPreference: readLocalPreference(storageKey),
  }));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const manualChangeRef = useRef(0);
  const queryOverrideRef = useRef(queryPreference === MAP_VIEW_3D);
  const saveQueueRef = useRef(Promise.resolve());
  const dataSaverEnabled = typeof navigator !== 'undefined' && Boolean(navigator.connection?.saveData);

  useEffect(() => {
    if (!available) {
      setViewState(MAP_VIEW_CLASSIC);
      return undefined;
    }

    const version = manualChangeRef.current;
    let cancelled = false;
    fetchMapViewPreference()
      .then((payload) => {
        if (cancelled || manualChangeRef.current !== version || queryOverrideRef.current) return;
        if (payload?.has_preference === false || !isMapViewPreference(payload?.preference)) return;
        const serverPreference = normalizeMapViewPreference(payload?.preference);
        runtime.mapViewPreference = serverPreference;
        writeLocalPreference(storageKey, serverPreference);
        setViewState(serverPreference);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [available, storageKey]);

  const setView = useCallback((nextPreference) => {
    const next = normalizeMapViewPreference(nextPreference);
    if (next === MAP_VIEW_3D && !available) return;
    manualChangeRef.current += 1;
    const changeVersion = manualChangeRef.current;
    queryOverrideRef.current = false;
    setViewState(next);
    setMessage(next === MAP_VIEW_3D ? '3D-Spielkarte aktiviert' : 'Klassische Übersicht aktiviert');
    setSaveError('');
    writeLocalPreference(storageKey, next);
    runtime.mapViewPreference = next;
    window.history.replaceState(window.history.state, document.title, urlWithoutMapViewOverride(window.location.href));

    setSaving(true);
    const saveTask = saveQueueRef.current.then(() => saveMapViewPreference(next));
    saveQueueRef.current = saveTask.catch(() => undefined);
    void saveTask
      .catch(() => {
        if (manualChangeRef.current === changeVersion) {
          setSaveError('Die Ansicht bleibt auf diesem Gerät aktiv. Die Kontoeinstellung konnte gerade nicht gespeichert werden.');
        }
      })
      .finally(() => {
        if (manualChangeRef.current === changeVersion) setSaving(false);
      });
  }, [available, storageKey]);

  const value = useMemo(() => ({
    available,
    view,
    setView,
    saving,
    message,
    saveError,
    dataSaverEnabled,
  }), [available, dataSaverEnabled, message, saveError, saving, setView, view]);

  return <MapViewContext.Provider value={value}>{children}</MapViewContext.Provider>;
}

export function useMapView() {
  const context = useContext(MapViewContext);
  if (!context) throw new Error('useMapView benötigt den MapViewProvider.');
  return context;
}

export { MAP_VIEW_3D, MAP_VIEW_CLASSIC, isMapViewPreference };
